import {Component, computed, effect, inject, OnInit} from '@angular/core';
import {FormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {toSignal} from '@angular/core/rxjs-interop';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {Button} from 'primeng/button';
import {DynamicDialogConfig, DynamicDialogRef} from 'primeng/dynamicdialog';
import {DirectusService, DirectusTranslatedItem} from '@frontend/directus';
import {ProviderDocumentVisibility} from '@frontend/shared-api';
import {FormFieldComponent} from '../../../inputs/form-field/form-field.component';
import {FormFieldSelectButtonComponent} from '../../../inputs/form-field/form-field-submit-button/form-field-select-button.component';
import {VISIBILITY_ORDER} from '../../../provider-document-library/provider-document-library.types';
import {
  DocumentUploadDialogData,
  DocumentUploadDialogResult,
  FINAL_DOCUMENT_STATUS_UUID,
} from '../document-upload-card.types';
import {
  fileExtensionValidator,
  forbiddenFileNameValidator,
} from './document-upload-dialog.validators';

@Component({
  imports: [
    ReactiveFormsModule,
    Button,
    TranslocoPipe,
    FormFieldComponent,
    FormFieldSelectButtonComponent,
  ],
  templateUrl: './document-upload-dialog.component.html',
})
export class DocumentUploadDialogComponent implements OnInit {
  private readonly directusService = inject(DirectusService);
  private readonly translocoService = inject(TranslocoService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly ref = inject(DynamicDialogRef);
  private readonly config = inject(DynamicDialogConfig);

  private readonly data: DocumentUploadDialogData = this.config.data;
  private readonly originalFilename: string =
    this.data.file?.name ?? this.data.originalFilename ?? '';
  private readonly defaultVersion: string = this.data.defaultVersion ?? 'v1.0';
  protected readonly showStatus: boolean = this.data.showStatus ?? true;

  protected readonly visibilityOptions: DirectusTranslatedItem[] = VISIBILITY_ORDER.map(
    (visibility) => ({
      id: visibility,
      translations: [
        {
          id: 0,
          relationship_id: visibility,
          language: this.translocoService.getActiveLang(),
          name: this.translocoService.translate('provider.documents.visibility.' + visibility),
        },
      ],
    }),
  );

  readonly form = this.formBuilder.nonNullable.group({
    originalFilename: [{value: '', disabled: true}],
    fileName: [
      '',
      [
        Validators.required,
        forbiddenFileNameValidator,
        fileExtensionValidator(this.originalFilename),
      ],
    ],
    version: [this.defaultVersion, [Validators.required]],
    statusUuid: ['', this.showStatus ? [Validators.required] : []],
    visibility: [
      this.data.visibility ?? ProviderDocumentVisibility.CONFIDENTIAL,
      [Validators.required],
    ],
  });

  protected readonly uploadDocumentStatusOptions = toSignal(
    this.directusService.loadDirectusDropdown('rfq_invitation_upload_document_status'),
    {initialValue: []},
  );

  private readonly statusUuid = toSignal(this.form.controls.statusUuid.valueChanges, {
    initialValue: this.form.controls.statusUuid.value,
  });

  protected readonly isVisibilityReadOnly = computed<boolean>(
    () => this.showStatus && this.statusUuid() !== FINAL_DOCUMENT_STATUS_UUID,
  );

  constructor() {
    effect(() => {
      this.applyConfidentialVisibility();
    });
  }

  ngOnInit(): void {
    if (this.data.isEdit) {
      this.form.patchValue({
        originalFilename: this.data.originalFilename ?? '',
        fileName: this.data.fileName ?? '',
        version: this.data.version ?? this.defaultVersion,
        statusUuid: this.data.documentStatusUuid ?? '',
      });

      if (this.data.isFileNameReadonly) {
        this.form.controls.fileName.disable();
      }
    } else if (this.data.file) {
      this.form.patchValue({
        originalFilename: this.data.file.name,
        fileName: this.data.file.name,
      });
    }

    this.form.controls.fileName.markAsTouched();
  }

  protected onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    const result: DocumentUploadDialogResult = {
      fileName: value.fileName,
      version: value.version,
      statusUuid: value.statusUuid,
      visibility: value.visibility,
    };

    this.ref.close(result);
  }

  protected onHideDialog(): void {
    this.ref.close();
  }

  private applyConfidentialVisibility(): void {
    if (this.isVisibilityReadOnly()) {
      this.form.controls.visibility.setValue(ProviderDocumentVisibility.CONFIDENTIAL);
    }
  }
}
